'use client';

import { AnimateNumber, Cursor } from 'motion-plus/react';
import { AnimatePresence, MotionConfig, motion } from 'motion/react';
import { useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis } from 'recharts';

import { ChartContainer, ChartTooltip } from '@shadCN/ui/chart';

const chartConfig = {
  value: {
    label: 'value',
    color: 'hsl(var(--chart-1))',
  },
};

interface MotionLineGraphProps {
  chartData: { month: string; value: number }[];
}
const MotionLineGraph: React.FC<MotionLineGraphProps> = ({ chartData }) => {
  const [percentageChange, setPercentageChange] = useState<number | null>(null);

  const startHover = (index: number) => {
    if (index > 0) {
      const currentValue = chartData[index].value;
      const previousValue = chartData[index - 1].value;
      const percentageChange =
        ((currentValue - previousValue) / previousValue) * 100;
      setPercentageChange(percentageChange);
    } else {
      setPercentageChange(null);
    }
  };

  const endHover = () => {
    setPercentageChange(null);
  };

  return (
    <ChartContainer config={chartConfig} onPointerLeave={endHover}>
      <LineChart
        accessibilityLayer
        data={chartData}
        margin={{
          left: 12,
          right: 12,
        }}>
        <CartesianGrid
          vertical={false}
          stroke="oklch(from var(--foreground) l c h / 10%)"
        />
        <XAxis
          dataKey="month"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={value => value.slice(0, 3)}
        />
        <ChartTooltip
          cursor={
            <AnimatePresence>
              {percentageChange !== null && (
                <Cursor
                  follow
                  initial={{ opacity: 0, scale: 0.5 }}
                  offset={{ x: 10, y: 10 }}
                  key="cursor">
                  <div style={numberContainer}>
                    <MotionConfig
                      transition={{
                        type: 'spring',
                        visualDuration: 0.6,
                        bounce: 0.2,
                      }}>
                      <motion.div
                        initial={false}
                        animate={{
                          rotate: percentageChange < 0 ? 180 : 0,
                          color: percentageChange < 0 ? '#ff0088' : '#4ff0b7',
                        }}
                        style={arrowStyle}>
                        ↑
                      </motion.div>
                      <AnimateNumber
                        initial={false}
                        animate={{
                          color: percentageChange < 0 ? '#ff0088' : '#4ff0b7',
                        }}
                        style={numberStyle}
                        format={{
                          notation: 'compact',
                          compactDisplay: 'short',
                        }}
                        suffix={'%'}>
                        {percentageChange}
                      </AnimateNumber>
                    </MotionConfig>
                  </div>
                </Cursor>
              )}
            </AnimatePresence>
          }
          content={e => {
            const index = chartData.findIndex(c => c.month === e.label);
            startHover(index);
            return <></>;
          }}
        />
        <Line
          dataKey="value"
          type="linear"
          // stroke="var(--color-value)"
          stroke="oklch(from var(--color-value) l c h / 60%)"
          strokeWidth={2}
          dot={true}
        />
      </LineChart>
    </ChartContainer>
  );
};

const numberContainer = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: 'var(--background)',
  border: '1px solid oklch(from var(--foreground) l c h / 10%)',
  padding: '6px 12px',
  borderRadius: 20,
  willChange: 'transform',
};

const numberStyle = {
  fontSize: '18px',
};

const arrowStyle = {
  fontSize: '18px',
  fontWeight: 'bold',
};

export default MotionLineGraph;
